import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { GlacierVideoExploration } from '../../components/BrandHomePage/MeaningfulExplorationSection/GlacierVideoExploration/GlacierVideoExploration';
import { TeamMembersCarousel } from '../../components/TeamMembersCarousel';
import { ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import { usePageContent, useSiteSettings } from '../../hooks/useContentful';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { PageToggleButton } from '../../components/shared/PageToggleButton/PageToggleButton';
import './OurStoryPage.css';

const OurStoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('our-story');
  const { data: siteSettingsData } = useSiteSettings();
  const pageContent = pageContentData?.[0]?.fields as any;
  const siteSettings = siteSettingsData?.[0]?.fields as any;
  
  const scrollCarousel = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 650, behavior: 'smooth' });
    }
  };

  // Initialize all interactive features
  useEffect(() => {
    const cleanup = initializeAllFeatures();
    return cleanup;
  }, []);

  return (
    <div className="our-story-page">
      {/* Navigation */}
      <SmartNavigation 
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
        forceWhiteAssets={true}
      />

      {/* Content Sections with Scroll Reveals - Same structure as homepage */}
      <div className="content-wrapper">
        
        {/* Timeline Carousel Section - Horizontal scrolling like Figma design */}
        <div data-scroll-section="timeline" className="diptych-carousel scroll-reveal-section" id="draggable-carousel">
          <div className="timeline-carousel-wrapper">
            
            {/* Opening Diptych - Text + Image */}
            <div className="timeline-diptych">
              <div className="timeline-text-block">
                <div className="timeline-intro-text">
                  <p>
                    {t('pages/ourStory:timeline.intro')}
                  </p>
                </div>
              </div>
              <div className="timeline-intro-image">
                <img src={images.ourStoryTimelineHero} alt="Awasi landscape" />
              </div>
            </div>

            {/* Timeline Carousel Container */}
            <div className="timeline-carousel-container">
              
              {/* 2010 - Calendar Text Block */}
              <div className="timeline-calendar-block">
                <div className="timeline-year-number">2010</div>
                <div className="timeline-content">
                  <h3>{t('pages/ourStory:timeline.2010.title')}</h3>
                  <p>
                    {t('pages/ourStory:timeline.2010.content')}
                  </p>
                </div>
              </div>

              {/* Video Section */}
              <div className="timeline-video-section">
                <div 
                  className="timeline-video"
                  style={{ 
                    backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), url('${images.ourStoryVideoBackground}')`
                  }}
                />
              </div>

              {/* Image Text Blurb Section */}
              <div className="timeline-image-text-section">
                <div className="timeline-section-image">
                  <img src={images.ourStoryAtacamaImage} alt="Awasi story" />
                </div>
                <div className="timeline-section-text">
                  <p>
                    {t('pages/ourStory:timeline.story.origin')}
                  </p>
                </div>
                <div className="timeline-section-body">
                  <p>
                    {t('pages/ourStory:timeline.story.philosophy')}
                  </p>
                  <p>
                  </p>
                </div>
              </div>

              {/* 2020 - Calendar Text Block */}
              <div className="timeline-calendar-block">
                <div className="timeline-year-number">2020</div>
                <div className="timeline-content">
                  <h3>New destinations</h3>
                  <p>
                    Between 2013 and 2018, Awasi expanded to include lodges in 
                    Patagonia and Iguazú, deepening its commitment to tailor-made 
                    explorations designed to create memorable, personal and authentic 
                    experiences. With each new opening, we
                  </p>
                </div>
              </div>

              {/* Collage Section */}
              <div className="timeline-collage-section">
                <div className="timeline-collage">
                  <img 
                    src={images.ourStoryCollage1} 
                    alt="Collage 1" 
                    className="collage-image collage-image-1"
                  />
                  <img 
                    src={images.ourStoryCollage2} 
                    alt="Collage 2" 
                    className="collage-image collage-image-2"
                  />
                  <img 
                    src={images.ourStoryCollage3} 
                    alt="Collage 3" 
                    className="collage-image collage-image-3"
                  />
                </div>
              </div>

              {/* Large Image */}
              <div className="timeline-large-image">
                <img src={images.ourStoryLargeLandscape} alt="Awasi landscape" />
              </div>

              {/* 2025 - Calendar Text Block */}
              <div className="timeline-calendar-block">
                <div className="timeline-year-number">2025</div>
                <div className="timeline-content">
                  <h3>{t('pages/ourStory:timeline.2025.title')}</h3>
                  <p>
                    {t('pages/ourStory:timeline.2025.content')}
                  </p>
                </div>
              </div>

            </div>
            
            {/* Carousel Navigation Arrow - REMOVED per user request */}
          </div>
        </div>

        {/* Meaningful Exploration Carousel Section */}
        <div data-scroll-section="exploration" className="meaningful-exploration-wrapper scroll-reveal-section">
          <GlacierVideoExploration />
        </div>

        {/* Full Viewport Waterfall Video Section */}
        <div data-scroll-section="waterfall-video" className="waterfall-video-section scroll-reveal-section">
          <div className="waterfall-video-background">
            <video 
              className="waterfall-video-element"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src="https://videos.ctfassets.net/hz1mviiqoqof/4J725DRkkj6GCsUPRltKxm/57a2b61783834fb53cd438548b3e5b84/team_awasi_video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {/* Optional mute button overlay */}
            <button className="video-mute-button">
              <div className="mute-icon">
                <img src={images.ourStoryMuteIcon} alt="Mute" />
              </div>
            </button>
          </div>
        </div>

        {/* Team & Talents Section - Horizontal Scroll Carousel */}
        <div data-scroll-section="team-talents" className="scroll-reveal-section">
          <TeamMembersCarousel />
        </div>

        {/* Equality, Diversity & Inclusion Section - Using CarbonNeutral Pattern */}
        <div data-scroll-section="equality" className="equality-diversity-section scroll-reveal-section">
          <div className="equality-diversity-container">
            <div className="equality-diversity-headline">
              <h2>{t('pages/ourStory:equality.title')}</h2>
            </div>
            <div className="equality-diversity-video-wrapper">
              <video 
                className="equality-diversity-video"
                autoPlay
                loop
                muted
                playsInline
              >
                <source src="https://videos.ctfassets.net/hz1mviiqoqof/6dh3iAO2quzL3vlJAE3lK8/e74360823de0f7d4f5f862a7a6a194ba/team_member_video.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="equality-diversity-content-box">
              <div className="equality-diversity-content-box-bg" />
              <div className="equality-diversity-content">
                <div className="equality-diversity-text-group">
                  <h3 className="equality-diversity-title">
                    {t('pages/ourStory:equality.inclusion.title')}
                  </h3>
                  <p className="equality-diversity-description">
                    {t('pages/ourStory:equality.inclusion.description')}
                  </p>
                </div>
                <HandDrawnButton
                  text={t('pages/ourStory:readMore')}
                  onClick={() => navigate('/about')}
                  variant="brand"
                  color="off-white"
                  animation="stroke-to-stroke"
                  size="normal"
                  className="equality-diversity-learn-more"
                />
              </div>
              <div className="equality-diversity-page-toggles">
                {[1, 2, 3, 4].map(pageNum => (
                  <PageToggleButton
                    key={pageNum}
                    number={pageNum}
                    isActive={currentPage === pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    color="off-white"
                    className="equality-diversity-page-toggle"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Press & Awards Section - Figma Exact Design */}
        <div data-scroll-section="awards" className="press-awards-section scroll-reveal-section">
          <div className="press-awards-container">
            <h2 className="press-awards-title">{t('pages/ourStory:awards.title')}</h2>
            
            <div className="press-awards-list">
              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.telegraph')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.telegraphProperties')}</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.condeNast')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.condeNastProperties')}</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.travelLeisure')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.travelLeisureProperties')}</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.hollywood')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.hollywoodProperties')}</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.tripadvisor')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.tripadvisorProperties')}</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">{t('pages/ourStory:awards.list.wineSpectator')}</div>
                <div className="award-properties">{t('pages/ourStory:awards.list.wineSpectatorProperties')}</div>
              </div>
            </div>
            
            <HandDrawnButton
              text={t('pages/ourStory:awards.seeMore')}
              onClick={() => navigate('/press-awards')}
              variant="brand"
              color="awasi"
              animation="stroke-to-stroke"
              size="normal"
              className="see-more-btn"
            />
          </div>
        </div>


      </div>

      {/* Instagram Section with Our Story specific images */}
      <InstagramSection 
        posts={[
          { src: images.ourStoryInstagram1, alt: "Patagonia mountain landscape" },
          { src: images.ourStoryInstagram2, alt: "Atacama desert landscape" },
          { src: images.ourStoryInstagram3, alt: "Natural hot spring" },
          { src: images.ourStoryInstagram4, alt: "Interior room with plants" }
        ]}
      />
      <Footer variant="default" />
    </div>
  );
};

export default OurStoryPage;